// ABOUTME: Bridge between the mounted MultiAthleteBatchProvider and the realtime refresh: on the
// ABOUTME: coach screen an event reloads the chunk fetchers that cover it, and nothing else.
import type { CalendarScope } from "@/model/calendar/cache/recordOwners"
import { registerRecordOwner } from "@/model/calendar/cache/recordOwners"
import type { RefreshOutcome } from "@/model/calendar/cache/refreshOutcome"
import { NOTHING_TO_DO, combineRefreshOutcomes } from "@/model/calendar/cache/refreshOutcome"

/**
 * The rows of the coach screen are fed by chunk fetchers, so refreshing that
 * screen means refetching the chunks an event touches — one request per chunk,
 * never one per athlete x week, which is the avalanche of 17.08.2026.
 *
 * Module state, like changedRequirementPairs: the realtime provider lives
 * above the calendar page and cannot read the batch context.
 */

/** `calendar` covers the calendarItems and requirements chunks, `health` the health ones. */
export type BatchChunkKind = "calendar" | "health"

/** `ranges` null means every live chunk of that kind — a full refresh. */
export type BatchChunkRefetch = (
  kind: BatchChunkKind,
  ranges: readonly CalendarScope[] | null,
) => Promise<RefreshOutcome>

/**
 * Every mounted provider, newest last — not a single slot. Two can briefly
 * coexist while a tree is rebuilt (the same reason registerProvider counts),
 * and with one slot the second one's unmount would erase the first, leaving
 * hasBatchChunks answering "no owner" while a provider is still on screen.
 */
const registered: BatchChunkRefetch[] = []

/**
 * The provider announces itself while mounted, in one place: the chunk-specific
 * registry below and the records registry every refresh path asks. `owns` says
 * which records these chunks hold — an athlete of a live chunk, on a day it
 * covers — and defaults to "all of them" for a caller that cannot narrow it.
 */
export function registerBatchChunkRefetch(
  refetch: BatchChunkRefetch,
  owns: (scope: CalendarScope) => boolean = () => true,
): () => void {
  registered.push(refetch)
  const unregisterOwner = registerRecordOwner({
    owns,
    refresh: async (scopes, families) =>
      combineRefreshOutcomes(await Promise.all(families.map((family) => refetch(family, scopes ?? null)))),
  })
  return () => {
    unregisterOwner()
    const at = registered.lastIndexOf(refetch)
    if (at !== -1) registered.splice(at, 1)
  }
}

/** The newest registration answers: on a rebuild it is the tree that stays. */
const currentRefetch = (): BatchChunkRefetch | null => registered.at(-1) ?? null

/**
 * Whether the coach screen's chunk fetchers are mounted — i.e. whether they own
 * the rows' records. A caller that must not write those records itself asks the
 * owner instead.
 */
export function hasBatchChunks(): boolean {
  return registered.length > 0
}

/**
 * Resolves at once when no provider is mounted. The provider decides which of
 * its chunks the ranges touch (athletes and dates), so an event for somebody
 * off screen makes no request — and reports which of the chunks it did ask
 * failed, for a caller that is watching a spinner.
 */
export function refetchBatchChunks(
  kind: BatchChunkKind,
  ranges: readonly CalendarScope[] | null,
): Promise<RefreshOutcome> {
  const refetch = currentRefetch()
  return refetch ? refetch(kind, ranges) : Promise.resolve(NOTHING_TO_DO)
}
